import * as React from 'react'
import {
  Step,
  Stepper,
  StepProps,
  StepperProps,
  DialogActions,
  DialogActionsProps,
  Box,
  Typography,
} from '../../components'
import { useEventCallback } from '../../hooks/useEventCallback'
import { useControlled } from '../../hooks/useControlled'

interface UseStepperProps {
  maxSteps: number
  dashStyle?: StepperProps['dashStyle']
  activeStep?: number
  setActiveStep?: React.Dispatch<React.SetStateAction<number>>
  defaultStep?: number
}

const useStepper = (config: UseStepperProps) => {
  const {
    maxSteps,
    dashStyle = { width: '64px', height: '4px', gap: '4px' },
    activeStep: activeStepProp,
    setActiveStep,
    defaultStep = 0,
  } = config

  const [activeStep, setActiveStepState] = useControlled({
    controlled: activeStepProp,
    default: defaultStep,
    name: 'activeStep',
    state: 'value',
  })

  //const [activeStep, setActiveStep] = React.useState(0)
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean
  }>({})

  const handleNext = useEventCallback(() => {
    const newCompleted = completed
    newCompleted[activeStep] = true
    setCompleted(newCompleted)
    setActiveStepState(prevActiveStep =>
      Math.min(prevActiveStep + 1, maxSteps - 1)
    )
    if (setActiveStep) {
      setActiveStep(prevActiveStep =>
        Math.min(prevActiveStep + 1, maxSteps - 1)
      )
    }
  })

  const handleBack = useEventCallback(() => {
    if (setActiveStep) {
      setActiveStep(prevActiveStep => Math.max(prevActiveStep - 1, 0))
    }
    setActiveStepState(prevActiveStep => Math.max(prevActiveStep - 1, 0))
    const newActiveStep = Math.max(activeStep - 1, 0)
    const newCompleted = completed
    newCompleted[newActiveStep] = false
    setCompleted(newCompleted)
  })

  const reset = useEventCallback(() => {
    if (setActiveStep) {
      setActiveStep(0)
    }
    setActiveStepState(0)
    setCompleted({})
  })

  const isLast = activeStep === maxSteps - 1

  const getStepperProps = React.useCallback(
    (props?: StepperProps) =>
      ({
        dashStyle: dashStyle,
        ...props,
        activeStep: activeStep,
        connector: null,
        variant: 'dash',
      }) as StepperProps,
    [dashStyle, activeStep]
  )

  const getStepProps = React.useCallback(
    (index: number, props?: StepProps) => {
      const boundedIndex = Math.max(Math.min(0, index), maxSteps - 1)
      return {
        key: index,
        Component: 'span',
        ...props,
        completed: completed[boundedIndex],
      } as StepProps
    },
    [completed, maxSteps]
  )

  const steps = React.useMemo(() => Array.from(Array(maxSteps)), [maxSteps])

  return {
    activeStep,
    handleNext,
    handleBack,
    reset,
    isLast,
    maxSteps,
    getStepperProps,
    getStepProps,
    steps,
  }
}

interface ChildrenFuncProps {
  handleNext: () => void
  handleBack: () => void
  reset: () => void
  isLast: boolean
  activeStep: number
}

interface RenderStepDescriptionProps {
  isLast: boolean
  activeStep: number
  maxSteps: number
}

export interface DialogActionsWithStepperProps
  extends Omit<DialogActionsProps, 'children'>,
    Pick<
      UseStepperProps,
      'maxSteps' | 'activeStep' | 'setActiveStep' | 'defaultStep'
    > {
  StepProps?: StepProps
  StepperProps?: StepperProps
  renderStepDescription?: (props: RenderStepDescriptionProps) => JSX.Element
  children?: React.ReactNode | ((props: ChildrenFuncProps) => React.ReactNode)
}

export const DialogActionsWithStepper = React.forwardRef<
  HTMLDivElement,
  DialogActionsWithStepperProps
>((props, ref) => {
  const {
    sx,
    StepProps,
    StepperProps,
    maxSteps,
    children,
    renderStepDescription,
    ...other
  } = props

  const {
    activeStep,
    handleBack,
    handleNext,
    isLast,
    reset,
    getStepperProps,
    getStepProps,
    steps,
  } = useStepper({
    maxSteps,
    dashStyle: StepperProps?.dashStyle,
    activeStep: other.activeStep,
    setActiveStep: other.setActiveStep,
    defaultStep: other.defaultStep,
  })

  const _renderStepDescription =
    typeof renderStepDescription === 'function'
      ? renderStepDescription
      : () => {
          return (
            <Typography
              gutterBottom={false}
              sx={{ display: 'block', textColor: 'neutral-600' }}
            >
              <Typography
                gutterBottom={false}
                sx={{ textColor: 'neutral-1000' }}
              >
                {activeStep + 1}
              </Typography>{' '}
              out of{' '}
              <Typography
                gutterBottom={false}
                sx={{ textColor: 'neutral-1000' }}
              >
                {steps.length}
              </Typography>{' '}
              Steps
            </Typography>
          )
        }

  return (
    <DialogActions
      ref={ref}
      sx={{
        display: 'flex',
        justifyContent: 'between',
        alignItems: 'center',
        ...sx,
      }}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gapRow: 8,
        }}
      >
        <Stepper {...getStepperProps(StepperProps)}>
          {steps.map(current => {
            /* eslint-disable-next-line */
            return <Step {...getStepProps(current, StepProps)} />
          })}
        </Stepper>
        {_renderStepDescription({ isLast, activeStep, maxSteps })}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gapCol: 16,
          }}
        >
          {typeof children === 'function'
            ? children({
                handleNext,
                handleBack,
                isLast,
                activeStep,
                reset,
              })
            : children}
        </Box>
      </Box>
    </DialogActions>
  )
})

DialogActionsWithStepper.displayName = 'DialogActionsWithStepper'
